import React, { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
    Button, Table, Thead, Tbody, Tr, Th, Td, Checkbox, Badge,
    HStack, useToast, Spinner, Center, Text, Flex, Box, Icon, IconButton,
    VStack, Card, CardBody, Heading, Divider
} from '@chakra-ui/react';
import { FiTrash2, FiRefreshCcw, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { format } from 'date-fns';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const RollCallModal = ({ isOpen, onClose, classData, onUpdate }) => {
    const [students, setStudents] = useState([]);
    const [attendanceMap, setAttendanceMap] = useState(new Map());
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const { user } = useAuth();
    const toast = useToast();

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen && classData?.id) {
            fetchData();
        } else {
            setStudents([]);
            setAttendanceMap(new Map());
            setSelectedIds([]);
        }
    }, [isOpen, classData]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            const classId = classData.id;

            const [studentsRes, attendanceRes] = await Promise.all([
                authAPI.getStudents({ limit: 1000, class_id: classId }),
                authAPI.attendance.getClassAttendance(classId, todayStr)
            ]);

            setStudents(studentsRes.data);

            const newMap = new Map();
            if (attendanceRes.data) {
                attendanceRes.data.forEach(record => {
                    newMap.set(record.student_id, record.status);
                });
            }
            setAttendanceMap(newMap);
            setSelectedIds([]);
        } catch (error) {
            console.error(error);
            toast({ title: "載入失敗", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    // --- Data Categorization (Aligned with RollCallPage) ---
    const pendingList = [];
    const processedList = [];

    students.forEach(s => {
        // Source of Truth: attendanceMap (Today's Record) OR Global Status
        let status = attendanceMap.get(s.id);

        // Logic Alignment:
        // 1. If explicitly Marked (in Map) -> Processed
        // 2. If Global Status is LEAVE -> Processed (as 'LEAVE')
        // 3. Otherwise -> Pending

        if (status) {
            processedList.push({ ...s, currentStatus: status });
        } else if (s.status === 'LEAVE') {
            processedList.push({ ...s, currentStatus: 'LEAVE', autoLeave: true });
        } else {
            pendingList.push(s);
        }
    });

    // --- Actions ---

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(pendingList.map(s => s.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(pid => pid !== id));
        }
    };

    const handleSubmit = async (status, targetIds = null) => {
        const idsToUpdate = targetIds || selectedIds;

        if (idsToUpdate.length === 0) {
            toast({ title: "未選擇學生", status: "warning" });
            return;
        }

        try {
            setSubmitting(true);
            const todayStr = format(new Date(), 'yyyy-MM-dd');

            await authAPI.attendance.batchUpdateClassAttendance(
                classData.id,
                idsToUpdate,
                todayStr,
                status
            );

            // Toast
            let message = "更新成功";
            if (status === 'PRESENT') message = `已標記 ${idsToUpdate.length} 位出席`;
            if (status === 'LEAVE') message = `已標記 ${idsToUpdate.length} 位請假`;
            if (status === 'RESET') message = `已重置 ${idsToUpdate.length} 位狀態`;

            toast({
                title: message,
                status: "success",
                duration: 2000
            });

            // Optimistic Update (UI Refresh without API call)
            const newMap = new Map(attendanceMap);

            if (status === 'RESET') {
                idsToUpdate.forEach(id => newMap.delete(id));
                // Important: Also update local student state to revert Global Status from 'LEAVE'
                // so they appear in Pending List immediately
                setStudents(prev => prev.map(s => {
                    if (idsToUpdate.includes(s.id)) {
                        // Optimistically set to NOT_ARRIVED so they drop out of "Global Leave" check
                        return { ...s, status: 'NOT_ARRIVED' };
                    }
                    return s;
                }));
            } else {
                idsToUpdate.forEach(id => newMap.set(id, status));

                // [Fix] Optimistically update Global Status for PRESENT/LEAVE
                setStudents(prev => prev.map(s => {
                    if (idsToUpdate.includes(s.id)) {
                        if (status === 'PRESENT') {
                            if (s.status === 'NOT_ARRIVED') return { ...s, status: 'ARRIVED' };
                        } else if (status === 'LEAVE') {
                            return { ...s, status: 'LEAVE' };
                        }
                    }
                    return s;
                }));
            }

            setAttendanceMap(newMap);
            setSelectedIds([]); // Clear selection

            if (onUpdate) onUpdate();

        } catch (error) {
            console.error(error);
            toast({ title: "操作失敗", description: error.response?.data?.detail, status: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    {classData?.name || '班級點名'}
                    <Text fontSize="md" color="gray.500" fontWeight="normal" mt={1}>
                        {format(new Date(), 'yyyy-MM-dd')}
                    </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6} bg="gray.50">
                    {loading ? (
                        <Center h="300px"><Spinner /></Center>
                    ) : (
                        <VStack spacing={6} align="stretch">

                            {/* Section 1: Pending (待點名) */}
                            <Box>
                                <HStack justify="space-between" mb={2}>
                                    <Heading size="sm" color="red.500">待點名 ({pendingList.length})</Heading>
                                    <HStack spacing={2}>
                                        <Button
                                            size="sm"
                                            colorScheme="yellow"
                                            onClick={() => handleSubmit('LEAVE')}
                                            isLoading={submitting}
                                            isDisabled={selectedIds.length === 0}
                                            leftIcon={<FiXCircle />}
                                        >
                                            標記請假
                                        </Button>
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={() => handleSubmit('PRESENT')}
                                            isLoading={submitting}
                                            isDisabled={selectedIds.length === 0}
                                            leftIcon={<FiCheckCircle />}
                                        >
                                            標記出席
                                        </Button>
                                    </HStack>
                                </HStack>

                                <Card variant="outline" bg="white" shadow="sm">
                                    <Table size="md">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th w="50px">
                                                    <Checkbox
                                                        isChecked={pendingList.length > 0 && selectedIds.length === pendingList.length}
                                                        isIndeterminate={selectedIds.length > 0 && selectedIds.length < pendingList.length}
                                                        onChange={handleSelectAll}
                                                        colorScheme="brand"
                                                    />
                                                </Th>
                                                <Th>學生姓名</Th>
                                                <Th>全校狀態</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {pendingList.length === 0 ? (
                                                <Tr>
                                                    <Td colSpan={3} textAlign="center" py={4} color="gray.400">
                                                        <Icon as={FiCheckCircle} mr={2} />
                                                        全員已處理完成
                                                    </Td>
                                                </Tr>
                                            ) : (
                                                pendingList.map(s => (
                                                    <Tr key={s.id} _hover={{ bg: 'blue.50' }}>
                                                        <Td>
                                                            <Checkbox
                                                                isChecked={selectedIds.includes(s.id)}
                                                                onChange={(e) => handleSelectOne(s.id, e.target.checked)}
                                                                colorScheme="brand"
                                                            />
                                                        </Td>
                                                        <Td fontWeight="bold" fontSize="md">{s.full_name}</Td>
                                                        <Td>
                                                            <Badge colorScheme={s.status === 'ARRIVED' ? 'green' : 'gray'}>
                                                                {s.status === 'ARRIVED' ? '已在校' : '未到校'}
                                                            </Badge>
                                                        </Td>
                                                    </Tr>
                                                ))
                                            )}
                                        </Tbody>
                                    </Table>
                                </Card>
                            </Box>

                            {/* Section 2: Processed (已處理) */}
                            {processedList.length > 0 && (
                                <Box>
                                    <Heading size="sm" color="gray.500" mb={2}>已處理 ({processedList.length})</Heading>
                                    <Card variant="outline" bg="gray.50">
                                        <CardBody p={0}>
                                            <Table size="sm">
                                                <Thead>
                                                    <Tr>
                                                        <Th>學生姓名</Th>
                                                        <Th>狀態</Th>
                                                        <Th textAlign="right">操作</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {processedList.map(s => (
                                                        <Tr key={s.id} bg="white">
                                                            <Td fontWeight="medium" color="gray.600">{s.full_name}</Td>
                                                            <Td>
                                                                {s.currentStatus === 'PRESENT' && (
                                                                    <Badge colorScheme="green">已出席</Badge>
                                                                )}
                                                                {s.currentStatus === 'LEAVE' && (
                                                                    <Badge colorScheme="purple">請假</Badge>
                                                                )}
                                                                {/* Handle other statuses if any */}
                                                            </Td>
                                                            <Td textAlign="right">
                                                                <IconButton
                                                                    icon={<FiRefreshCcw />}
                                                                    aria-label="Reset"
                                                                    size="xs"
                                                                    variant="ghost"
                                                                    colorScheme="gray"
                                                                    onClick={() => handleSubmit('RESET', [s.id])}
                                                                    isDisabled={submitting}
                                                                    title="重置"
                                                                />
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </Box>
                            )}

                        </VStack>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default RollCallModal;
